import React, { useState, useEffect } from 'react';
import { ShoppingCart, Plus, Package, TrendingUp, Calendar, FileText, DollarSign } from 'lucide-react';
import { API_URL } from '../services/api';

const AchatsMatieres = () => {
  const [showModal, setShowModal] = useState(false);
  const [prixActuels, setPrixActuels] = useState({ or: 0, chaine: 0, mousqueton: 0 });
  const [achatsRecents, setAchatsRecents] = useState([]);
  const [formData, setFormData] = useState({
    type_matiere: '',
    quantite: '',
    prix_achat_unitaire: '',
    fournisseur_nom: '',
    date_achat: new Date().toISOString().split('T')[0],
    numero_facture: '',
    mode_paiement: 'Virement',
    emplacement: '',
    notes: ''
  });

  useEffect(() => {
    loadPrixActuels();
    loadAchatsRecents();
  }, []);

  const loadPrixActuels = async () => {
    try {
      const response = await fetch(`${API_URL}/stock-matieres/prix/actuel`);
      const data = await response.json();
      setPrixActuels(data);
    } catch (error) {
      console.error('Erreur chargement prix:', error);
    }
  };

  const loadAchatsRecents = async () => {
    try {
      const response = await fetch(`${API_URL}/stock-matieres/stock`);
      const data = await response.json();
      // Trier par date d'achat décroissante et prendre les 10 derniers
      const recent = data.sort((a, b) => new Date(b.date_achat) - new Date(a.date_achat)).slice(0, 10);
      setAchatsRecents(recent);
    } catch (error) {
      console.error('Erreur chargement achats:', error);
    }
  };

  const genererReference = () => {
    const prefixes = {
      'OR 18K': 'OR18K',
      'ARGENT': 'ARG',
      'CHAINE': 'CHAIN',
      'MOUSQUETON': 'MOUSQ'
    };
    const prefix = prefixes[formData.type_matiere] || 'MAT';
    const year = new Date().getFullYear();
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `${prefix}-${year}-${random}`;
  };

  const calculerPrixSuggere = () => {
    const prix = {
      'OR 18K': prixActuels.or,
      'CHAINE': prixActuels.chaine,
      'MOUSQUETON': prixActuels.mousqueton,
      'ARGENT': 75
    };
    return prix[formData.type_matiere] || 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const reference = genererReference();
    const categorie = formData.type_matiere === 'OR 18K' || formData.type_matiere === 'ARGENT' ? 'METAL' : 'METAL';
    
    const dataToSend = {
      reference,
      categorie,
      type_matiere: formData.type_matiere,
      quantite: parseFloat(formData.quantite),
      quantite_minimum: parseFloat(formData.quantite) * 0.1, // 10% comme seuil minimum
      unite: 'g',
      fournisseur_nom: formData.fournisseur_nom,
      prix_achat_unitaire: parseFloat(formData.prix_achat_unitaire),
      date_achat: formData.date_achat,
      numero_facture: formData.numero_facture,
      emplacement: formData.emplacement,
      notes: formData.notes
    };

    try {
      const response = await fetch(`${API_URL}/stock-matieres/stock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        alert('Achat enregistré avec succès !');
        setShowModal(false);
        resetForm();
        loadAchatsRecents();
      } else {
        const error = await response.json();
        alert(`Erreur: ${error.error}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'enregistrement');
    }
  };

  const resetForm = () => {
    setFormData({
      type_matiere: '',
      quantite: '',
      prix_achat_unitaire: '',
      fournisseur_nom: '',
      date_achat: new Date().toISOString().split('T')[0],
      numero_facture: '',
      mode_paiement: 'Virement',
      emplacement: '',
      notes: ''
    });
  };

  const calculerTotal = () => {
    const quantite = parseFloat(formData.quantite) || 0;
    const prix = parseFloat(formData.prix_achat_unitaire) || 0;
    return (quantite * prix).toFixed(2);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* En-tête */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center space-x-3">
              <ShoppingCart className="h-7 w-7 sm:h-8 sm:w-8 text-purple-600" />
              <span>Achats Matières Premières</span>
            </h1>
            <p className="text-gray-600 mt-1">Gérez vos achats d'or, argent, chaînes et mousquetons</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all flex items-center justify-center space-x-2 shadow-lg"
          >
            <Plus className="h-5 w-5" />
            <span>Nouvel Achat</span>
          </button>
        </div>

        {/* Prix actuels */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <Package size={32} />
              <TrendingUp size={24} />
            </div>
            <p className="text-sm opacity-90">Or 18K</p>
            <p className="text-3xl font-bold">{prixActuels.or.toFixed(2)} DH/g</p>
          </div>
          <div className="bg-gradient-to-br from-gray-300 to-gray-500 rounded-lg p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <Package size={32} />
              <TrendingUp size={24} />
            </div>
            <p className="text-sm opacity-90">Argent 925</p>
            <p className="text-3xl font-bold">75.00 DH/g</p>
          </div>
          <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <Package size={32} />
              <TrendingUp size={24} />
            </div>
            <p className="text-sm opacity-90">Chaîne</p>
            <p className="text-3xl font-bold">{prixActuels.chaine.toFixed(2)} DH/g</p>
          </div>
          <div className="bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-lg p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <Package size={32} />
              <TrendingUp size={24} />
            </div>
            <p className="text-sm opacity-90">Mousqueton</p>
            <p className="text-3xl font-bold">{prixActuels.mousqueton.toFixed(2)} DH/g</p>
          </div>
        </div>

        {/* Achats récents */}
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Achats Récents</h2>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {achatsRecents.length === 0 ? (
              <p className="text-center text-gray-500 py-6">Aucun achat enregistré</p>
            ) : achatsRecents.map((achat) => (
              <div key={achat.id} className="bg-white rounded-lg shadow-sm border-l-4 border-purple-400 p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="font-bold text-gray-900">{achat.reference}</div>
                    <div className="text-xs text-gray-500">{new Date(achat.date_achat).toLocaleDateString()}</div>
                  </div>
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                    {achat.type_matiere}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Quantité: </span><span className="font-medium">{parseFloat(achat.quantite_initiale).toFixed(3)} g</span></div>
                  <div><span className="text-gray-500">Prix/g: </span><span className="font-medium">{parseFloat(achat.prix_achat_unitaire).toFixed(2)} DH</span></div>
                  <div><span className="text-gray-500">Total: </span><span className="font-bold text-purple-700">{parseFloat(achat.prix_achat_total).toFixed(2)} DH</span></div>
                  <div><span className="text-gray-500">Fourn.: </span><span className="font-medium">{achat.fournisseur_nom || '—'}</span></div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Référence</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantité</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix/g</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fournisseur</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {achatsRecents.map((achat) => (
                  <tr key={achat.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(achat.date_achat).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-medium text-gray-900">{achat.reference}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                        {achat.type_matiere}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {parseFloat(achat.quantite_initiale).toFixed(3)} g
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {parseFloat(achat.prix_achat_unitaire).toFixed(2)} DH
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {parseFloat(achat.prix_achat_total).toFixed(2)} DH
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {achat.fournisseur_nom || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* end hidden md:block */}
        </div>

        {/* Modal Nouvel Achat */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
            <div className="bg-white rounded-t-2xl sm:rounded-lg p-4 sm:p-6 w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
              <h3 className="text-2xl font-bold mb-6 text-gray-800">Nouvel Achat de Matières Premières</h3>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type de Matière *</label>
                    <select
                      required
                      value={formData.type_matiere}
                      onChange={(e) => {
                        setFormData({
                          ...formData, 
                          type_matiere: e.target.value,
                          prix_achat_unitaire: calculerPrixSuggere()
                        });
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-lg"
                    >
                      <option value="">Sélectionner une matière...</option>
                      <option value="OR 18K">OR 18K</option>
                      <option value="ARGENT">ARGENT 925</option>
                      <option value="CHAINE">CHAINE</option>
                      <option value="MOUSQUETON">MOUSQUETON</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantité (grammes) *</label>
                    <input
                      type="text" inputMode="decimal" onFocus={e => e.target.select()}
                      step="0.001"
                      required
                      value={formData.quantite}
                      onChange={(e) => setFormData({...formData, quantite: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="0.000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prix/gramme (DH) *</label>
                    <input
                      type="text" inputMode="decimal" onFocus={e => e.target.select()}
                      step="0.01"
                      required
                      value={formData.prix_achat_unitaire}
                      onChange={(e) => setFormData({...formData, prix_achat_unitaire: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="0.00"
                    />
                    {formData.type_matiere && (
                      <p className="text-xs text-gray-500 mt-1">
                        Prix suggéré: {calculerPrixSuggere().toFixed(2)} DH/g
                      </p>
                    )}
                  </div>

                  <div className="col-span-2 bg-purple-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-medium text-gray-700">Total à payer:</span>
                      <span className="text-2xl font-bold text-purple-600">{calculerTotal()} DH</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fournisseur *</label>
                    <input
                      type="text"
                      required
                      value={formData.fournisseur_nom}
                      onChange={(e) => setFormData({...formData, fournisseur_nom: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="Nom du fournisseur"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date d'achat *</label>
                    <input
                      type="date"
                      required
                      value={formData.date_achat}
                      onChange={(e) => setFormData({...formData, date_achat: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">N° Facture</label>
                    <input
                      type="text"
                      value={formData.numero_facture}
                      onChange={(e) => setFormData({...formData, numero_facture: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="FAC-2026-001"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mode de paiement</label>
                    <select
                      value={formData.mode_paiement}
                      onChange={(e) => setFormData({...formData, mode_paiement: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="Virement">Virement</option>
                      <option value="Espèces">Espèces</option>
                      <option value="Chèque">Chèque</option>
                    </select>
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Emplacement</label>
                    <input
                      type="text"
                      value={formData.emplacement}
                      onChange={(e) => setFormData({...formData, emplacement: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="Coffre, Tiroir A, etc."
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      rows="3"
                      placeholder="Informations complémentaires..."
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); resetForm(); }}
                    className="w-full sm:w-auto px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg"
                  >
                    Enregistrer l'Achat
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AchatsMatieres;
